/**
 * LeadPacingTile
 *
 * Replaces the static "Good Leads MTD" tile in the Forecast page's
 * Sales Department Analysis card. Renders the pacing widget — progress bar,
 * status pill, caption ("X.X leads/cal-day · Y.Y% 6-mo rolling close · N-unit
 * goal"), sub-tiles (units progress, MTD close rate with rolling delta), and
 * NEW/USED gross variance chips.
 *
 * When `leadsData.pacing` is missing (e.g. older API response, locationId=0,
 * backend transient error), falls back to the legacy big-number display.
 */

import React from 'react';
import { SalesLeadsPacingResponse } from '../../services/api';
import { PacingProgressBar, paceStatus } from '../shared/PacingProgressBar';

const numFmt = new Intl.NumberFormat('en-US');

function fmtPct(v: number | null, decimals = 1): string {
  return v === null || v === undefined ? '—' : `${v.toFixed(decimals)}%`;
}

function fmtPctSigned(v: number | null): string {
  if (v === null || v === undefined) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(1)}%`;
}

/** Same green/amber/rose tiering as the Lead Pacing Report's variance chips. */
function varianceColor(v: number | null): string {
  if (v === null) return 'text-gray-400 bg-gray-50 border-gray-200';
  if (v >= 0) return 'text-green-700 bg-green-50 border-green-200';
  if (v > -5) return 'text-amber-700 bg-amber-50 border-amber-200';
  return 'text-rose-700 bg-rose-50 border-rose-200';
}

interface LeadPacingTileProps {
  leadsData: SalesLeadsPacingResponse | null;
  loading: boolean;
  monthName: string;
}

export const LeadPacingTile: React.FC<LeadPacingTileProps> = ({ leadsData, loading, monthName }) => {
  const pacing = leadsData?.pacing;
  const mtdLeads = leadsData?.mtdGoodLeads ?? 0;

  if (loading) {
    return (
      <div className="flex-1 bg-blue-50 border border-blue-200 rounded-lg p-6 flex items-center justify-center min-h-[16rem]">
        <div className="text-gray-400 text-sm">Loading…</div>
      </div>
    );
  }

  // Fallback: pacing absent (locationId=0, transient error, older backend) — keep
  // the previous big-number display so the card still gives the headline number.
  if (!pacing) {
    return (
      <div className="flex-1 bg-blue-50 border border-blue-200 rounded-lg p-6 flex flex-col items-center justify-center min-h-[16rem]">
        <div className="text-5xl font-bold text-blue-700">{numFmt.format(mtdLeads)}</div>
        <div className="text-sm text-gray-600 mt-2">sales-relevant Good Leads in {monthName}</div>
        <div className="text-xs text-gray-500 mt-1">
          Excludes Bad/Duplicate · Internet, Phone, WebsiteChat, Referral, Walk-in
        </div>
      </div>
    );
  }

  // Pacing math: compute segment values exactly the same way the Lead Pacing
  // Report does — keeps the two views consistent.
  const ahead = pacing.totalLeadsNeeded !== null && pacing.projectedEomLeads >= pacing.totalLeadsNeeded;
  const gap = pacing.totalLeadsNeeded !== null && !ahead
    ? pacing.totalLeadsNeeded - pacing.projectedEomLeads
    : 0;
  const atPace = Math.max(0, pacing.projectedEomLeads - pacing.mtdLeads);
  const status = paceStatus(pacing.incrementalLeadsNeeded);

  // MTD close rate vs 6-mo rolling — delta pill on the close-rate sub-tile.
  const closeDelta = (pacing.closingRatioPct !== null && pacing.rollingClosingRatioPct !== null)
    ? Math.round((pacing.closingRatioPct - pacing.rollingClosingRatioPct) * 10) / 10
    : null;
  let deltaPill: { label: string; cls: string } | null = null;
  if (closeDelta !== null) {
    if (closeDelta < 0) deltaPill = { label: `▼ ${Math.abs(closeDelta).toFixed(1)}pts`, cls: 'bg-rose-50 text-rose-700 border-rose-200' };
    else if (closeDelta > 0) deltaPill = { label: `▲ ${closeDelta.toFixed(1)}pts`, cls: 'bg-green-50 text-green-700 border-green-200' };
    else deltaPill = { label: '= 0pts', cls: 'bg-gray-50 text-gray-600 border-gray-200' };
  }

  const unitPct = pacing.unitGoal > 0
    ? Math.min(100, (pacing.mtdUnits / pacing.unitGoal) * 100)
    : 0;

  return (
    <div className="flex-1 bg-blue-50 border border-blue-200 rounded-lg p-4 flex flex-col gap-3 min-h-[16rem]">
      {/* Header row: title + status pill */}
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium text-gray-700">Good Leads MTD</div>
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${status.pillClass}`}>
          {status.label}
        </span>
      </div>

      {/* Progress bar */}
      <PacingProgressBar mtdLeads={pacing.mtdLeads} atPace={atPace} gap={gap} size="short" minSegmentPct={14} />

      {/* End-of-month projection summary */}
      <div className="text-xs text-gray-600">
        <span className="font-semibold text-blue-700">{numFmt.format(pacing.projectedEomLeads)}</span> at pace
        {pacing.totalLeadsNeeded !== null && (
          <>
            {' · '}
            <span className="font-semibold">{numFmt.format(pacing.totalLeadsNeeded)}</span> needed
          </>
        )}
      </div>

      {/* Caption */}
      <div className="text-xs text-gray-600 leading-relaxed">
        {pacing.avgLeadsPerCalDay.toFixed(1)} leads/cal-day · {
          pacing.rollingClosingRatioPct !== null
            ? `${fmtPct(pacing.rollingClosingRatioPct)} 6-mo rolling close`
            : pacing.closingRatioPct !== null
              ? `${fmtPct(pacing.closingRatioPct)} MTD close (no rolling history)`
              : 'no close rate available'
        } · {pacing.unitGoal > 0 ? `${numFmt.format(pacing.unitGoal)}-unit goal` : 'no unit goal'}
      </div>

      {/* Sub-tiles: Units sold / Close rate MTD */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-white border border-gray-200 rounded-md p-2">
          <div className="text-[10px] uppercase tracking-wide text-gray-500">Units sold</div>
          <div className="text-lg font-bold text-gray-900">
            {numFmt.format(pacing.mtdUnits)}
            <span className="text-xs font-normal text-gray-500"> / {pacing.unitGoal > 0 ? numFmt.format(pacing.unitGoal) : '—'}</span>
          </div>
          {pacing.unitGoal > 0 && (
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-1">
              <div className="h-full bg-blue-600" style={{ width: `${unitPct}%` }} />
            </div>
          )}
        </div>
        <div className="bg-white border border-gray-200 rounded-md p-2">
          <div className="text-[10px] uppercase tracking-wide text-gray-500">Close rate MTD</div>
          <div className="text-lg font-bold text-gray-900">{fmtPct(pacing.closingRatioPct)}</div>
          {deltaPill && (
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border mt-0.5 ${deltaPill.cls}`}>
              {deltaPill.label} vs 6-mo
            </span>
          )}
        </div>
      </div>

      {/* Gross variance chips */}
      <div className="flex gap-2 text-xs">
        <span className={`flex-1 inline-flex items-center justify-between px-2 py-1 rounded-md border ${varianceColor(pacing.newDeptGrossVariancePct)}`}>
          <span className="font-medium">NEW gross</span>
          <span className="font-bold">{fmtPctSigned(pacing.newDeptGrossVariancePct)}</span>
        </span>
        <span className={`flex-1 inline-flex items-center justify-between px-2 py-1 rounded-md border ${varianceColor(pacing.usedDeptGrossVariancePct)}`}>
          <span className="font-medium">USED gross</span>
          <span className="font-bold">{fmtPctSigned(pacing.usedDeptGrossVariancePct)}</span>
        </span>
      </div>
    </div>
  );
};
